import type { FundingRateBlotterPanelConfig } from '@src/types/LayoutConfig';
import { FormControlSizes, HStack, IconButton, IconName } from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildMissingMetricsErrorMessage,
  sameStringList,
  SignalsModuleShell,
  useExchangeDisplayNameMap,
  useMissingMetrics,
  useSignalsAssetMap,
} from '../core';
import {
  FUNDING_RATE_FREQUENCY_LABELS,
  FUNDING_RATE_METRIC,
  FUNDING_RATE_METRIC_LABEL,
} from '../HistoricalFundingRate/tokens';
import {
  FundingRateBlotterView,
  type FundingRateGroupBy,
  type FundingRateUpdateMode,
} from './FundingRateBlotter.types';
import { FundingRateBlotterConfigView } from './FundingRateBlotterConfigView';
import { FundingRateBlotterToolbar } from './FundingRateBlotterToolbar';
import { FundingRateListView } from './FundingRateListView';
import { FundingRateMatrixView } from './FundingRateMatrixView';
import { FundingRateUpdateModeToggle } from './FundingRateUpdateModeToggle';
import {
  FUNDING_RATE_BLOTTER_DEFAULT_CM_ASSET_IDS,
  FUNDING_RATE_BLOTTER_DEFAULT_GROUP_BY,
  FUNDING_RATE_BLOTTER_DEFAULT_UPDATE_MODE,
  FUNDING_RATE_BLOTTER_DEFAULT_VIEW,
  FUNDING_RATE_UPDATE_MODE_FREQUENCY,
  OPEN_INTEREST_METRIC,
  OPEN_INTEREST_METRIC_LABEL,
} from './tokens';
import { useFundingRateBlotterData } from './useFundingRateBlotterData';

const ASSET_MAP_METRICS = [FUNDING_RATE_METRIC];

interface FundingRateBlotterModuleProps {
  params: FundingRateBlotterPanelConfig;
  onParamsChange: (partial: Partial<FundingRateBlotterPanelConfig>) => void;
}

type PanelView = 'blotter' | 'config';

/**
 * Top-level module. Owns the session-only view state (blotter vs config, List vs Matrix, group by)
 * and delegates persisted config (asset list, update mode, default view) to the parent. The panel
 * is renamed via the tab context menu, so there is no title handling here.
 */
export function FundingRateBlotterModule({ params, onParamsChange }: FundingRateBlotterModuleProps) {
  const updateMode = params.updateMode ?? FUNDING_RATE_BLOTTER_DEFAULT_UPDATE_MODE;
  const defaultView = params.defaultView ?? FUNDING_RATE_BLOTTER_DEFAULT_VIEW;
  // Shown in the rate-cell hover card's "Period" row — the update mode's display label ("1H"/"1D").
  const periodLabel = FUNDING_RATE_FREQUENCY_LABELS[FUNDING_RATE_UPDATE_MODE_FREQUENCY[updateMode]];

  const [panelView, setPanelView] = useState<PanelView>(() =>
    params.settingsInitialOpen === true ? 'config' : 'blotter'
  );
  // Session-only view, seeded from the configured default. The user can toggle it at runtime.
  const [runtimeView, setRuntimeView] = useState<FundingRateBlotterView>(defaultView);
  const [groupBy, setGroupBy] = useState<FundingRateGroupBy>(FUNDING_RATE_BLOTTER_DEFAULT_GROUP_BY);
  // Setup is a one-shot flow gating the initial config screen only; update mode is a live, persisted
  // setting editable at any time (from Settings or the toolbar toggle), not gated by this.
  const [isSetupPhase, setIsSetupPhase] = useState(() => params.settingsInitialOpen === true);

  // Persist the creation-time defaults explicitly on first mount so the instance's configuration is
  // concrete rather than depending indefinitely on code-level defaults.
  // settingsInitialOpen is intentionally NOT cleared here — it's cleared on setup completion (Done)
  // so a reload mid-setup resumes the config view instead of dropping into the blotter.
  useEffect(() => {
    const patch: Partial<FundingRateBlotterPanelConfig> = {};
    if (params.updateMode == null) {
      patch.updateMode = FUNDING_RATE_BLOTTER_DEFAULT_UPDATE_MODE;
    }
    if (params.defaultView == null) {
      patch.defaultView = FUNDING_RATE_BLOTTER_DEFAULT_VIEW;
    }
    if (Object.keys(patch).length > 0) {
      onParamsChange(patch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cmAssetIds = useMemo(() => params.assetList ?? FUNDING_RATE_BLOTTER_DEFAULT_CM_ASSET_IDS, [params.assetList]);

  const {
    talosSymbols: availableTalosSymbols,
    cmAssetIdByTalosSymbol,
    talosSymbolByCmAssetId,
    catalogEntries,
    isLoading: isAssetsLoading,
    isError: isAssetsError,
  } = useSignalsAssetMap(ASSET_MAP_METRICS);

  // No frequency passed: FUNDING_RATE_METRIC can be fetched hourly or daily (the update-mode toggle),
  // so entitlement is checked at any cadence rather than the currently-selected one. This only covers
  // the funding rate metric, which gates the asset universe — open interest entitlement is checked
  // separately below, from the snapshot itself, since it isn't part of the asset-map catalog request.
  const missingMetrics = useMissingMetrics(catalogEntries, ASSET_MAP_METRICS, isAssetsLoading);

  const { displayNameByExchangeId } = useExchangeDisplayNameMap();

  // Drop any persisted asset no longer supported by the metric (or absent from the org's currencies),
  // and materialize the hard-coded default basket into params on first mount.
  useEffect(() => {
    if (isAssetsLoading || isAssetsError) {
      return;
    }
    const filtered = cmAssetIds.filter(id => talosSymbolByCmAssetId.has(id));
    if (filtered.length !== cmAssetIds.length || params.assetList == null) {
      onParamsChange({ assetList: filtered });
    }
  }, [isAssetsLoading, isAssetsError, talosSymbolByCmAssetId, cmAssetIds, params.assetList, onParamsChange]);

  const { rows, isLoading, isError, isTruncated, unentitledMetrics } = useFundingRateBlotterData({
    cmAssetIds,
    symbolByCmAssetId: talosSymbolByCmAssetId,
    exchangeNameByExchangeId: displayNameByExchangeId,
    updateMode,
    // Don't fetch until the user confirms setup — avoids requests for the default basket they may change.
    enabled: !isSetupPhase,
  });

  // Two independent entitlement checks feed the same message: the funding rate metric is gated via
  // the catalog (its query silently omits the metric rather than erroring), while open interest is
  // fetched alongside it in the same snapshot request and can't be checked against that catalog at
  // all (it isn't part of the asset-map catalog query) — so it's detected from the snapshot response
  // instead, via the same silent-omission signal.
  const missingMetricLabels = useMemo(() => {
    const labels: string[] = [];
    // A failed catalog request also leaves catalogEntries empty, which looks identical to a genuine
    // entitlement gap to useMissingMetrics — only trust it once the catalog actually loaded.
    if (missingMetrics.length > 0 && !isAssetsError) {
      labels.push(FUNDING_RATE_METRIC_LABEL);
    }
    if (unentitledMetrics.includes(OPEN_INTEREST_METRIC)) {
      labels.push(OPEN_INTEREST_METRIC_LABEL);
    }
    return labels;
  }, [missingMetrics, isAssetsError, unentitledMetrics]);
  const hasMissingMetrics = missingMetricLabels.length > 0;

  const selectedTalosSymbols = useMemo(() => {
    const symbols: string[] = [];
    for (const id of cmAssetIds) {
      const symbol = talosSymbolByCmAssetId.get(id);
      if (symbol != null) {
        symbols.push(symbol);
      }
    }
    return symbols;
  }, [cmAssetIds, talosSymbolByCmAssetId]);

  const handleSelectedTalosSymbolsChange = useCallback(
    (talosSymbols: string[]) => {
      const nextCmAssetIds: string[] = [];
      for (const symbol of talosSymbols) {
        const id = cmAssetIdByTalosSymbol.get(symbol);
        if (id != null) {
          nextCmAssetIds.push(id);
        }
      }
      if (!sameStringList(nextCmAssetIds, cmAssetIds)) {
        onParamsChange({ assetList: nextCmAssetIds });
      }
    },
    [cmAssetIdByTalosSymbol, cmAssetIds, onParamsChange]
  );

  const handleUpdateModeChange = useCallback(
    (mode: FundingRateUpdateMode) => {
      if (mode !== updateMode) {
        onParamsChange({ updateMode: mode });
      }
    },
    [updateMode, onParamsChange]
  );

  const handleDefaultViewChange = useCallback(
    (view: FundingRateBlotterView) => {
      if (view !== defaultView) {
        onParamsChange({ defaultView: view });
      }
    },
    [defaultView, onParamsChange]
  );

  const handleOpenConfig = useCallback(() => setPanelView('config'), []);
  const handleCloseConfig = useCallback(() => {
    // On initial setup, open in the configured default view (the user may have just chosen it).
    // On a later edit, keep whatever view the user is currently on — the runtime toggle owns that;
    // "default view" governs how the instance loads, not every config close.
    if (isSetupPhase) {
      setRuntimeView(defaultView);
    }
    // Setup is complete: start fetching and clear the one-shot flag now (not on mount) so a reload
    // mid-setup resumes config rather than dropping into the blotter.
    setIsSetupPhase(false);
    if (params.settingsInitialOpen) {
      onParamsChange({ settingsInitialOpen: false });
    }
    setPanelView('blotter');
  }, [isSetupPhase, defaultView, params.settingsInitialOpen, onParamsChange]);

  if (panelView === 'config') {
    return (
      <FundingRateBlotterConfigView
        selectedTalosSymbols={selectedTalosSymbols}
        availableTalosSymbols={availableTalosSymbols}
        onSelectedTalosSymbolsChange={handleSelectedTalosSymbolsChange}
        updateMode={updateMode}
        onUpdateModeChange={handleUpdateModeChange}
        defaultView={defaultView}
        onDefaultViewChange={handleDefaultViewChange}
        onClose={handleCloseConfig}
      />
    );
  }

  // Asset map only affects the display symbol (falls back to the CM id), so let its load block the
  // grid only while there's nothing to show yet.
  const isInitialLoading = isLoading || (isAssetsLoading && rows.length === 0);
  // isAssetsError is folded in too: a real asset-map failure was previously invisible here (only
  // guarded the asset-filtering effect above), silently rendering an empty-looking blotter with no
  // explanation. hasMissingMetrics is handled separately, via isPermissionError below.
  const isErrorOrTruncated = isError || isTruncated || isAssetsError;
  // A truncated response means the snapshot is silently incomplete, so surface it as a load failure
  // rather than rendering partial data. Both truncation sources (the global metric catalog and the
  // wildcard snapshot) sit far below their page caps, so this is defensive — and the copy stays
  // generic because the catalog request is global, so "remove some assets" couldn't change it.
  const errorMessage = hasMissingMetrics
    ? buildMissingMetricsErrorMessage(missingMetricLabels)
    : 'Failed to load funding rate data.';

  return (
    <SignalsModuleShell
      header={
        <FundingRateBlotterToolbar
          view={runtimeView}
          onViewChange={setRuntimeView}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          availableTalosSymbols={availableTalosSymbols}
          selectedTalosSymbols={selectedTalosSymbols}
          onSelectedTalosSymbolsChange={handleSelectedTalosSymbolsChange}
        />
      }
      controls={
        <HStack gap="spacing8">
          <FundingRateUpdateModeToggle
            updateMode={updateMode}
            onUpdateModeChange={handleUpdateModeChange}
            groupTestId="funding-rate-blotter-update-mode-toggle"
            buttonTestIdPrefix="funding-rate-blotter-update-mode"
          />
          <IconButton
            icon={IconName.Cog}
            onClick={handleOpenConfig}
            size={FormControlSizes.Small}
            data-testid="funding-rate-blotter-open-config"
          />
        </HStack>
      }
      isLoading={isInitialLoading}
      isError={isErrorOrTruncated}
      errorMessage={errorMessage}
      isPermissionError={hasMissingMetrics}
    >
      {runtimeView === FundingRateBlotterView.Matrix ? (
        <FundingRateMatrixView rows={rows} periodLabel={periodLabel} />
      ) : (
        <FundingRateListView rows={rows} groupBy={groupBy} periodLabel={periodLabel} />
      )}
    </SignalsModuleShell>
  );
}
